import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router } from '@angular/router';
import { FetchPolicy } from '@apollo/client/core';
import { AppConfig } from '@app/core/app.config';
import { Asset, AssetInput } from '@app/core/model/entities/asset/asset';
import { ActivationEndService } from '@app/features/main/activation-end.service';
import { SelectParams } from '@app/shared/grid/datasource-utils';
import ApiService from '@services/api.service';
import { AppManager } from '@services/managers/app.manager';
import { gql } from 'apollo-angular';
import { plainToInstance } from 'class-transformer';
import { EMPTY, expand, from, Observable, Subject, switchMap } from 'rxjs';
import { map, reduce, tap, toArray } from 'rxjs/operators';

@Injectable({providedIn: 'root'})
export class AssetsService {
  private sidePanelToggleSubject = new Subject<Asset | null>();

  private addAssetSubject = new Subject<Asset>();
  private updateAssetSubject = new Subject<Asset>();
  private deleteAssetsSubject = new Subject<Asset[]>();

  private apiService = inject(ApiService);
  private appManager = inject(AppManager);
  private appConfig = inject(AppConfig);
  private router = inject(Router);
  private activationEndService = inject(ActivationEndService);

  private readonly assetInfoGraphqlFragment = gql`
    fragment AssetInfo on Asset {
      id
      name
      identifier
      typeId
      computedProperties
      properties @include(if: $fetchFullData)
      assetState @include(if: $fetchFullData)
      organizationId @include(if: $fetchFullData)
      creationActor @include(if: $fetchFullData) {
        actorId
        name
      }
      creationDate @include(if: $fetchFullData)
      lastChangeActor @include(if: $fetchFullData) {
        actorId
        name
      }
      lastChangeDate @include(if: $fetchFullData)
      dataDate @include(if: $fetchFullData)
    }`;

  /**
   * Loads assets page based on the provided select parameters.
   *
   * This function performs a GraphQL query to fetch assets based on the provided select parameters.
   * The select parameters include sorting, filtering, grouping, pagination.
   * It also fetches information about users who created and last updated the assets.
   *
   * @param selectParams The parameters for the select query. These include sorting, filtering, grouping, pagination, and locale settings.
   * @param fetchFullData Optional. Determines if we want to query all fields in the Asset entity, or just a minimal subset
   * @return An Observable emitting an array of Asset objects, each with additional user information.
   */
  public loadAssetsPage(selectParams: SelectParams, fetchFullData: boolean = true): Observable<Asset[]> {
    const query = gql`
      query Assets($organizationId: String!, $selectParams: SelectParams!, $fetchFullData: Boolean! = true) {
        assets(organizationId: $organizationId, selectParams: $selectParams) {
          ...AssetInfo
        }
      }
      ${this.assetInfoGraphqlFragment}
    `;
    const variables = {
      organizationId: this.appManager.currentOrganization.id,
      selectParams: selectParams,
      fetchFullData: fetchFullData
    };

    return this.apiService.query({query, variables})
      .pipe(map(data => plainToInstance(Asset, data['assets'] as Asset[])));
  }

  /**
   * Loads all assets pages based on the provided select parameters.
   *
   * This function performs a GraphQL query to fetch assets based on the provided select parameters.
   * The select parameters include sorting, filtering, grouping.
   * It also fetches information about users who created and last updated the assets.
   *
   * @param selectParams The parameters for the select query. These include sorting, filtering, grouping, and locale settings.
   * @param fetchFullData Optional. Determines if we want to query all fields in the Asset entity, or just a minimal subset
   * @return An Observable emitting an array of Asset objects, each with additional user information.
   */
  public loadAllAssets(selectParams: SelectParams, fetchFullData: boolean = true): Observable<Asset[]> {
    const pageSize = this.appConfig.MAX_REQUEST_PAGE_SIZE;
    const params = {...selectParams, start: 0, count: pageSize};
    return this.loadAssetsPage(params, fetchFullData)
      .pipe(
        expand((assets, i) => {
          if (assets.length < pageSize) {
            return EMPTY;
          }
          params.start = (i + 1) * pageSize;
          return this.loadAssetsPage(params, fetchFullData);
        }),
        reduce((all, page) => {
          all.push(...page);
          return all;
        }, [] as Asset[])
      );
  }

  /**
   * Emits an Asset whenever it is updated.
   */
  public get assetAdded$(): Observable<Asset> {
    return this.addAssetSubject.asObservable();
  }

  /**
   * Emits an Asset whenever it is updated.
   */
  public get assetUpdated$(): Observable<Asset> {
    return this.updateAssetSubject.asObservable();
  }

  /**
   * Emits an Asset whenever it is deleted.
   */
  public get assetsDeleted$(): Observable<Asset[]> {
    return this.deleteAssetsSubject.asObservable();
  }

  /**
   * Emits an Asset whenever the side panel opens, or null whenever it closes.
   */
  public get sidePanelToggle$(): Observable<Asset | null> {
    return this.sidePanelToggleSubject.asObservable();
  }

  /**
   * Fetch an Asset by its ID
   * @param assetId ID of the Asset to return.
   * @param fetchPolicy GraphQL query fetch policy, default is 'network-only'.
   * @return Asset
   */
  public loadAsset(assetId: string, fetchPolicy: FetchPolicy = 'network-only'): Observable<Asset> {
    const query = gql`
      query AssetById($assetId: String!, $fetchFullData: Boolean! = true) {
        asset(id: $assetId) {
          ...AssetInfo
        }
      }
      ${this.assetInfoGraphqlFragment}
    `;
    const variables = {assetId};
    return this.apiService.query({query, variables, fetchPolicy})
      .pipe(
        map(data => plainToInstance(Asset, data['asset'])),
        tap(() => {
          if (fetchPolicy === 'cache-first') {
            this.activationEndService.getRefreshHeaderSubject().next();
          }
        })
      );
  }

  /**
   * Make an API request to create a new Asset, then add it to the grid.
   * @param assetInput Data to use for creating a new Asset.
   * @return Created Asset.
   */
  public createAsset(assetInput: AssetInput): Observable<Asset> {
    const mutation = gql`
      mutation CreateAsset($assetInput: CreateAssetInput!, $fetchFullData: Boolean! = true) {
        createAsset(assetInput: $assetInput) {
          ...AssetInfo
        }
      }
      ${this.assetInfoGraphqlFragment}
    `;
    const variables = {
      assetInput: {...assetInput, organizationId: this.appManager.currentOrganization.id}
    };

    return this.apiService.mutate({mutation, variables})
      .pipe(map(data => plainToInstance(Asset, data['createAsset'])));
  }

  /**
   * Check whether the current Organization has any Asset.
   * @return True if the Organization has at least one Asset or false otherwise.
   */
  public organizationHasAssets(): Observable<boolean> {
    const query = gql`
      query OrganizationHasAssets($organizationId: String!) {
        organizationHasAssets(organizationId: $organizationId)
      }
    `;
    const variables = {
      organizationId: this.appManager.currentOrganization.id
    };

    return this.apiService.query({query, variables})
      .pipe(map(data => data['organizationHasAssets'] as boolean));
  }

  /**
   * Make an API request to update an Asset.
   * @param asset Existing Asset.
   * @param assetInput Data for updating the Asset.
   * @return Updated Asset.
   */
  public updateAsset(asset: Asset, assetInput: AssetInput): Observable<Asset> {
    const mutation = gql`
      mutation UpdateAsset($assetInput: UpdateAssetInput!, $fetchFullData: Boolean! = true) {
        updateAsset(assetInput: $assetInput) {
          ...AssetInfo
        }
      }
      ${this.assetInfoGraphqlFragment}
    `;
    const variables = {
      assetInput: {
        id: asset.id,
        name: asset.name,
        assetState: asset.assetState,
        ...assetInput
      }
    };

    return this.apiService.mutate({mutation, variables})
      .pipe(
        map(data => plainToInstance(Asset, data['updateAsset'])),
        tap(updatedAsset => this.updateAssetSubject.next(updatedAsset))
      );
  }

  /**
   * Make a bulk API request to update a list of Asset.
   * @param assets List of objects containing asset and assetInput to update.
   * @return Updated Assets.
   */
  public updateAssets(assets: { asset: Asset, assetInput: AssetInput }[]): Observable<Asset[]> {
    const mutation = gql`
      mutation UpdateAssets($assetInputs: [UpdateAssetInput!]!, $fetchFullData: Boolean! = true) {
        updateAssets(assetInputs: $assetInputs) {
          ...AssetInfo
        }
      }${this.assetInfoGraphqlFragment}
    `;
    const variables = {
      assetInputs: assets.map(({asset: {id, name, assetState}, assetInput}) => {
        return {id, name, assetState, ...assetInput};
      })
    };

    return this.apiService.mutate({mutation, variables})
      .pipe(
        map(data => plainToInstance(Asset, data['updateAssets'] as Asset[])),
        switchMap(assets => from(assets)),
        tap(updatedAsset => this.updateAssetSubject.next(updatedAsset)),
        toArray()
      );
  }

  /**
   * Make an API request to delete the Asset.
   * @param assets Asset to delete.
   * @return True if Asset is successfully deleted, false otherwise.
   */
  public deleteAssets(assets: Asset[]): Observable<boolean> {
    const mutation = gql`
      mutation DeleteAsset($assetIds: [String!]!) {
        deleteAssets(ids: $assetIds)
      }
    `;
    const variables = {
      assetIds: assets.map(asset => asset.id)
    };

    return this.apiService.mutate({mutation, variables})
      .pipe(
        map(data => data['deleteAssets'] as boolean),
        tap(success => success && this.deleteAssetsSubject.next(assets))
      );
  }

  /**
   * Open the side panel displaying the Asset.
   * @param asset Asset to be displayed in the side panel.
   */
  public openAssetSidePanel(asset: Asset): void {
    this.sidePanelToggleSubject.next(asset);
  }

  /**
   * Close the side panel.
   */
  public closeAssetSidePanel(): void {
    this.sidePanelToggleSubject.next(null);
  }

  /**
   * Navigate to the Asset's sheet and open the requested tab.
   * @param assetId ID of the Asset to navigate to.
   * @param tabName Name of the tab to open after navigating to the Asset's sheet.
   * @return Promise that resolves to true if navigation succeeds, false if navigation fails or rejects on error.
   */
  public navigateToAssetSheet(assetId: string, tabName: string): Promise<boolean> {
    return this.router.navigate([
      'organization',
      this.appManager.currentOrganization.id,
      'assets',
      'asset-sheet',
      assetId,
      tabName
    ]);
  }
}
